import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Grid, Button, Divider,
  TextField, Select, MenuItem, FormControl, Slider, CircularProgress,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import GridViewIcon from '@mui/icons-material/GridView';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TuneIcon from '@mui/icons-material/Tune';
import { AiActions } from '../../Services/_actions/index.js';

// ── Constants ────────────────────────────────────────────────────────────────
const PURPLE = 'primary.main';
const FONTS = ['Calibri','Arial','Helvetica','Georgia','Cambria','Trebuchet MS','Verdana','Palatino','Times New Roman','Impact','Tahoma'];
const TEMPLATES = [
  { id: 'classic',   label: 'Classic',     desc: 'Title + bullets, clean and simple' },
  { id: 'sidebar',   label: 'Sidebar',     desc: 'Vertical color sidebar with brand name' },
  { id: 'split',     label: 'Split 50/50', desc: 'Title block on left, content on right' },
  { id: 'header',    label: 'Header band', desc: 'Brand color band across the top' },
  { id: 'cardgrid',  label: 'Card grid',   desc: 'Each bullet in its own card' },
  { id: 'editorial', label: 'Editorial',   desc: 'Magazine-style with large numbers' },
];
const LOGO_OPTIONS = [
  { id: 'none',      label: 'No logo',         desc: "Don't include the company logo" },
  { id: 'header',    label: 'Header',           desc: 'Top corner of every slide' },
  { id: 'footer',    label: 'Footer',           desc: 'Bottom corner of every slide' },
  { id: 'titleonly', label: 'Title slide only', desc: 'Logo only on cover & closing' },
];
const SLIDE_STYLES = [
  { id: 'minimal',   label: 'Minimal',   desc: 'Lots of white space, thin lines' },
  { id: 'bold',      label: 'Bold',      desc: 'Large titles, strong color blocks' },
  { id: 'corporate', label: 'Corporate', desc: 'Clean, professional, balanced' },
  { id: 'playful',   label: 'Playful',   desc: 'Rounded shapes, vibrant accents' },
];
const TONES = ['Formal','Conversational','Energetic','Academic'];
const DENSITIES = ['Low (3 per slide)','Medium (5 per slide)','High (7 per slide)'];

// ── Default brand state ───────────────────────────────────────────────────────
const DEFAULT_BRAND = {
  // Template tab
  template: 'sidebar',
  logo_placement: 'header',
  // Colors tab
  color_primary: '#6D28D9',
  color_accent: '#F59E0B',
  color_background: '#FFFFFF',
  color_body: '#1F2937',
  // Typography tab
  heading_font: 'Calibri',
  body_font: 'Calibri',
  title_size: 42,
  body_size: 16,
  // Style & Tone tab
  slide_style: 'playful',
  tone: 'Formal',
  density: 'Medium (5 per slide)',
};

type BrandState = typeof DEFAULT_BRAND;

// ── Helper to read org id from localStorage ──────────────────────────────────
const getOrganisationId = (): string => {
  const userInfo = localStorage?.getItem('userInfo');
  return userInfo
    ? JSON.parse(userInfo)?.profile?.organisation?.organisation_id ?? ""
    : "";
};

// ── Shared sub-components ────────────────────────────────────────────────────
const Card: React.FC<{ selected: boolean; onClick: () => void; children: React.ReactNode }> = ({ selected, onClick, children }) => (
  <Box
    onClick={onClick}
    sx={{
      border: `2px solid ${selected ? PURPLE : '#e8e8f0'}`,
      borderRadius: '12px', cursor: 'pointer',
      bgcolor: selected ? 'rgba(124,92,252,0.04)' : '#fff',
      overflow: 'hidden',
      '&:hover': { borderColor: PURPLE },
    }}
  >
    {children}
  </Box>
);

const StyledSelect: React.FC<{ label?: string; value: string; options: string[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <Box>
    {label && <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#555', mb: 0.8 }}>{label}</Typography>}
    <FormControl fullWidth>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          height: 48, borderRadius: '10px', fontSize: 13.5,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0ee' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PURPLE },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PURPLE, borderWidth: 1 },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: '12px', mt: 0.5, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', maxHeight: 280,
              '& .MuiMenuItem-root': { fontSize: 13.5, py: 1.2, '&:hover': { bgcolor: 'rgba(124,92,252,0.06)' }, '&.Mui-selected': { bgcolor: 'rgba(124,92,252,0.1)', color: PURPLE, fontWeight: 600 } },
            },
          },
        }}
      >
        {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </Select>
    </FormControl>
  </Box>
);

const PtSlider: React.FC<{ label: string; value: number; min: number; max: number; onChange: (v: number) => void }> = ({ label, value, min, max, onChange }) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{value}pt</Typography>
    </Box>
    <Slider
      value={value} onChange={(_, v) => onChange(v as number)} min={min} max={max}
      sx={{ color: PURPLE, '& .MuiSlider-thumb': { width: 18, height: 18, border: '2px solid #fff', boxShadow: `0 0 0 2px ${PURPLE}` }, '& .MuiSlider-rail': { bgcolor: '#e0e0ee' } }}
    />
  </Box>
);

// ── Tab panels (receive brand + setter) ──────────────────────────────────────

interface TabProps { brand: BrandState; setBrand: React.Dispatch<React.SetStateAction<BrandState>> }

const TemplateTab: React.FC<TabProps> = ({ brand, setBrand }) => (
  <Box>
    <Typography sx={{ fontSize: 13, color: '#666', mb: 2 }}>Pick a slide layout. Colors and fonts stay the same.</Typography>
    <Grid container spacing={1.5} sx={{ mb: 3 }}>
      {TEMPLATES.map((t) => (
        <Grid item xs={6} key={t.id}>
          <Card selected={brand.template === t.id} onClick={() => setBrand((p) => ({ ...p, template: t.id }))}>
            <Box sx={{ p: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{t.label}</Typography>
              <Typography sx={{ fontSize: 11.5, color: '#9e9e9e' }}>{t.desc}</Typography>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>

    <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1.5 }}>Company logo placement</Typography>
    <Grid container spacing={1.5} sx={{ mb: 1 }}>
      {LOGO_OPTIONS.map((o) => (
        <Grid item xs={6} key={o.id}>
          <Card selected={brand.logo_placement === o.id} onClick={() => setBrand((p) => ({ ...p, logo_placement: o.id }))}>
            <Box sx={{ p: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{o.label}</Typography>
              <Typography sx={{ fontSize: 11.5, color: '#9e9e9e' }}>{o.desc}</Typography>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
    <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>Uses the logo you uploaded for your company.</Typography>
  </Box>
);

const ColorsTab: React.FC<TabProps> = ({ brand, setBrand }) => {
  // Each color swatch needs its own ref to open the native picker
  const refs = {
    color_primary:    useRef<HTMLInputElement>(null),
    color_accent:     useRef<HTMLInputElement>(null),
    color_background: useRef<HTMLInputElement>(null),
    color_body:       useRef<HTMLInputElement>(null),
  };

  const fields: [keyof BrandState, string][] = [
    ['color_primary',    'Primary (titles, headers)'],
    ['color_accent',     'Accent (highlights, bars)'],
    ['color_background', 'Slide background'],
    ['color_body',       'Body text'],
  ];

  const set = (k: keyof BrandState) => (v: string) =>
    setBrand((p) => ({ ...p, [k]: v }));

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {fields.map(([k, label]) => (
          <Grid item xs={6} key={k}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#555', mb: 0.8 }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0ee', borderRadius: '10px', overflow: 'hidden', height: 48 }}>
              <Box
                onClick={() => refs[k as keyof typeof refs]?.current?.click()}
                sx={{ width: 48, height: '100%', bgcolor: brand[k] as string, cursor: 'pointer', borderRight: '1px solid #e0e0ee', flexShrink: 0 }}
              />
              <input
                ref={refs[k as keyof typeof refs]}
                type="color"
                value={brand[k] as string}
                onChange={(e) => set(k)(e.target.value.toUpperCase())}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
              />
              <TextField
                value={brand[k] as string}
                onChange={(e) => set(k)(e.target.value)}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                inputProps={{ maxLength: 7, style: { fontSize: 13.5, paddingLeft: 12 } }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Live preview */}
      <Box sx={{ border: '1px solid #e8e8f0', borderRadius: '12px', p: 2.5, bgcolor: brand.color_background }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: brand.color_primary, mb: 0.8 }}>Sample slide title</Typography>
        <Typography sx={{ fontSize: 13.5, color: brand.color_body, mb: 1.5 }}>A short body line so you can preview the contrast.</Typography>
        <Box sx={{ width: 80, height: 4, bgcolor: brand.color_accent, borderRadius: 2 }} />
      </Box>
    </Box>
  );
};

const TypographyTab: React.FC<TabProps> = ({ brand, setBrand }) => (
  <Box>
    <Grid container spacing={2} sx={{ mb: 2.5 }}>
      <Grid item xs={6}>
        <StyledSelect label="Heading font" value={brand.heading_font} options={FONTS} onChange={(v) => setBrand((p) => ({ ...p, heading_font: v }))} />
      </Grid>
      <Grid item xs={6}>
        <StyledSelect label="Body font" value={brand.body_font} options={FONTS} onChange={(v) => setBrand((p) => ({ ...p, body_font: v }))} />
      </Grid>
    </Grid>
    <PtSlider label="Title size" value={brand.title_size} min={20} max={72} onChange={(v) => setBrand((p) => ({ ...p, title_size: v }))} />
    <PtSlider label="Body size"  value={brand.body_size}  min={8}  max={32} onChange={(v) => setBrand((p) => ({ ...p, body_size: v }))} />

    {/* Live preview */}
    <Box sx={{ border: '1px solid #e8e8f0', borderRadius: '12px', p: 2.5 }}>
      <Typography sx={{ fontFamily: brand.heading_font, fontSize: Math.min(brand.title_size * 0.45, 28), fontWeight: 700, mb: 0.8 }}>
        Sample slide title
      </Typography>
      <Typography sx={{ fontFamily: brand.body_font, fontSize: Math.min(brand.body_size * 0.85, 16), color: '#555' }}>
        A short body line so you can preview the font pairing.
      </Typography>
    </Box>
  </Box>
);

const StyleToneTab: React.FC<TabProps> = ({ brand, setBrand }) => (
  <Box>
    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.5 }}>Slide style</Typography>
    <Grid container spacing={1.5} sx={{ mb: 3 }}>
      {SLIDE_STYLES.map((s) => (
        <Grid item xs={6} key={s.id}>
          <Card selected={brand.slide_style === s.id} onClick={() => setBrand((p) => ({ ...p, slide_style: s.id }))}>
            <Box sx={{ p: 1.8 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{s.label}</Typography>
              <Typography sx={{ fontSize: 12, color: '#9e9e9e' }}>{s.desc}</Typography>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <StyledSelect label="Tone of voice" value={brand.tone} options={TONES} onChange={(v) => setBrand((p) => ({ ...p, tone: v }))} />
      </Grid>
      <Grid item xs={6}>
        <StyledSelect label="Bullet density" value={brand.density} options={DENSITIES} onChange={(v) => setBrand((p) => ({ ...p, density: v }))} />
      </Grid>
    </Grid>
  </Box>
);

// ── Tab config ────────────────────────────────────────────────────────────────
const TAB_CONFIG = [
  { icon: <GridViewIcon sx={{ fontSize: 15 }} />,   label: 'Template',     Panel: TemplateTab },
  { icon: <PaletteIcon sx={{ fontSize: 15 }} />,    label: 'Colors',       Panel: ColorsTab },
  { icon: <TextFieldsIcon sx={{ fontSize: 15 }} />, label: 'Typography',   Panel: TypographyTab },
  { icon: <TuneIcon sx={{ fontSize: 15 }} />,       label: 'Style & Tone', Panel: StyleToneTab },
];

// ── Main component ────────────────────────────────────────────────────────────
const BrandPreferencesContent: React.FC<{
  onCancel: () => void;
  onSave: () => void;
  onReset: () => void;
  companyName?: string;
}> = ({ onCancel, onSave, onReset, companyName }) => {
  const action = AiActions();
  const organisationId = getOrganisationId();

  const [tab, setTab] = useState(0);
  const [brand, setBrand] = useState<BrandState>({ ...DEFAULT_BRAND });

  const [isFetching, setIsFetching] = useState(true);
  const [isSaving, setIsSaving]   = useState(false);
  const [recordExists, setRecordExists] = useState<boolean | null>(null);

  // ── Fetch existing brand prefs on mount ─────────────────────────────────
  useEffect(() => {
    if (!organisationId) { setIsFetching(false); return; }

    (async () => {
      setIsFetching(true);
      try {
        const res = await action.getOrganizationInstruction(organisationId);
        if (res && !res?.general_error) {
          setRecordExists(true);
          // Merge only the brand keys that exist in the response
          setBrand((prev) => {
            const next = { ...prev };
            (Object.keys(DEFAULT_BRAND) as (keyof BrandState)[]).forEach((k) => {
              if (res[k] !== undefined && res[k] !== null) {
                (next as any)[k] = res[k];
              }
            });
            return next;
          });
        } else {
          setRecordExists(false);
        }
      } catch {
        setRecordExists(false);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [organisationId]);

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!organisationId) { onSave(); return; }

    setIsSaving(true);
    try {
      const payload = {
        organization_id: organisationId,
       brand_preferences: {
    ...brand,
  },
        ...(companyName ? { company_name: companyName } : {}),
      };

      if (recordExists) {
        await action.updateOrganizationInstruction(payload);
      } else {
        await action.createOrganizationInstruction(payload);
        setRecordExists(true);
      }
    } catch (err) {
      console.error('Failed to save brand preferences:', err);
    } finally {
      setIsSaving(false);
      onSave();
    }
  };

  // ── Reset ────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setBrand({ ...DEFAULT_BRAND });
    onReset();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const { Panel } = TAB_CONFIG[tab];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5 }}>
        <PaletteIcon sx={{ color: 'primary.main', fontSize: 22 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Brand Preferences</Typography>
      </Box>
      <Typography sx={{ fontSize: 13, color: '#9e9e9e', mb: 2 }}>
        One-time setup. Every PPT generated for{' '}
        <Box component="span" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
          {companyName || 'your company'}
        </Box>{' '}
        will use these colors, fonts and style.
      </Typography>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2.5, bgcolor: '#f6f6f9', borderRadius: '10px', px: 0.5,
          '& .MuiTab-root': { textTransform: 'none', fontSize: 13, fontWeight: 500, minHeight: 36, py: 0.8, px: 1.5, color: '#666', borderRadius: '8px' },
          '& .Mui-selected': { color: `${PURPLE} !important`, fontWeight: 700 },
          '& .MuiTabs-indicator': { bgcolor: PURPLE, height: 2, borderRadius: 2 },
        }}
      >
        {TAB_CONFIG.map((t) => (
          <Tab key={t.label} icon={t.icon} iconPosition="start" label={t.label} />
        ))}
      </Tabs>

      {/* Tab content */}
      {isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={30} />
        </Box>
      ) : (
        <Panel brand={brand} setBrand={setBrand} />
      )}

      <Divider sx={{ mt: 2, mb: 2 }} />

      {/* Footer actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, alignItems: 'center' }}>
        <Button
          onClick={handleReset}
          sx={{ textTransform: 'none', color: '#666', fontSize: 13 }}
        >
          ↺ Reset
        </Button>
        <Button
          onClick={onCancel}
          sx={{ textTransform: 'none', color: '#444', fontSize: 13, border: '1px solid #e0e0ee', borderRadius: '8px', px: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isFetching || isSaving}
          startIcon={isSaving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          sx={{
            textTransform: 'none', bgcolor: PURPLE, color: '#fff', fontWeight: 600,
            fontSize: 13, borderRadius: '8px', px: 2.5,
            '&:hover': { bgcolor: '#6a4de0' },
            '&.Mui-disabled': { bgcolor: '#bbb', color: '#fff' },
          }}
        >
          {isSaving ? 'Saving…' : recordExists ? 'Update preferences' : 'Save preferences'}
        </Button>
      </Box>
    </Box>
  );
};

export default BrandPreferencesContent;
